// Demo-mode branches use baked pools from data/demo-data.ts.
import { useEffect, useState } from 'react';
import { Tablet } from 'lucide-react';
import { M, SectionLabel } from '../components/primitives.js';
import {
  canopusCommandService,
  type CanopusSIAggregates,
  type CanopusSIModuleCounts,
  type CanopusSIPeriod,
  type CanopusStaffStatus,
} from '../../../services/canopusCommand.js';
import type { InsightsSurgeon } from '../../../services/insights.js';
// Demo SI aggregates / TFI rows from baked pools.
import { getDemoSIAggregates, getDemoSITfiRows, type DemoSITfiRow } from '../data/demo-data.js';
import { capitalizeName } from './staff.js';
import './surgical-inquiries.css';
import './si-tfi.css';

type ModuleLabel =
  | 'Reference Guide'
  | 'Pick List'
  | 'Tray Layout'
  | 'Room Layout'
  | 'Procedure Notes';

type ModulePctMap = Record<keyof CanopusSIModuleCounts, number>;

type DetailView = 'surgeon' | 'staff' | 'or' | 'specialty' | 'procedure' | 'profile';

interface PeriodData {
  total: number;
  avgPerCase: number;
  liveCaseRatio: number;
  cases: number;
  perProfile: string;
  offCase: number;
}

interface ModuleCountsByLabel {
  'Reference Guide': number;
  'Pick List': number;
  'Tray Layout': number;
  'Room Layout': number;
  'Procedure Notes': number;
}

interface ORRow {
  or: string;
  surgeon: string;
  procedure: string;
  total: number;
  refGuide: number;
  pickList: number;
  trayLayout: number;
  roomLayout: number;
  procNotes: number;
  liveCase: boolean;
  staff: string;
  cases: number;
  avgPerCase: number;
  topModule: string;
}

interface DetailRow {
  name: string;
  total: number;
  avgPerCase: number;
  cases: number;
  topModule: string;
  liveCaseRatio: number;
  trend?: number[];
  position?: string;
  surgeon?: string;
  offCase?: number;
  specialty?: string;
  procedure?: string;
  variant?: string;
  // True when this staff row is actually a shared OR device (merged per platform).
  isDevice?: boolean;
}

interface ProfileRow {
  name: string;
  surgeon: string;
  procedure: string;
  avgPerCase: number;
  cases: number;
  monthlyAvg: number;
  total: number;
  modules: ModulePctMap;
  topModule: string;
}

// ─── Adapters: backend aggregates → UI shape ─────────────────────

function adaptAggregatesToPeriodData(overview: CanopusSIAggregates['overview']): PeriodData {
  return {
    total: overview.total,
    avgPerCase: overview.avgPerCase,
    liveCaseRatio: overview.liveCaseRatio,
    cases: overview.cases,
    perProfile: '--', // SI / Profile / Month — not yet computed server-side
    offCase: overview.offCase,
  };
}

function adaptModuleCounts(byModule: CanopusSIModuleCounts): ModuleCountsByLabel {
  // Shape matches the mockup's `todayByModule` keyed by display label.
  return {
    'Reference Guide': byModule.refGuide,
    'Pick List': byModule.pickList,
    'Tray Layout': byModule.trayLayout,
    'Room Layout': byModule.roomLayout,
    'Procedure Notes': byModule.procNotes,
  };
}

function adaptByOR(byOR: CanopusSIAggregates['byOR']): ORRow[] {
  return byOR.map((r) => ({
    or: r.roomName,
    surgeon: r.surgeonName ?? '—',
    procedure: r.procedureName ?? '—',
    total: r.total,
    refGuide: r.modules.refGuide,
    pickList: r.modules.pickList,
    trayLayout: r.modules.trayLayout,
    roomLayout: r.modules.roomLayout,
    procNotes: r.modules.procNotes,
    liveCase: r.liveCase,
    staff: '—', // server side does not expose "primary staff for room" yet
    cases: r.cases,
    avgPerCase: r.avgPerCase,
    topModule: r.topModule,
  }));
}

function adaptBySurgeon(bySurgeon: CanopusSIAggregates['bySurgeon']): DetailRow[] {
  return bySurgeon.map((s) => ({
    name: s.name,
    total: s.total,
    avgPerCase: s.avgPerCase,
    cases: s.cases,
    topModule: s.topModule,
    liveCaseRatio: 0, // per-surgeon live ratio not computed today
    trend: [],
  }));
}

function adaptBySpecialty(bySpecialty: CanopusSIAggregates['bySpecialty']): DetailRow[] {
  return bySpecialty.map((s) => ({
    name: s.name,
    total: s.total,
    avgPerCase: s.avgPerCase,
    cases: s.cases,
    topModule: s.topModule,
    liveCaseRatio: 0,
  }));
}

function adaptByProcedure(byProcedure: CanopusSIAggregates['byProcedure']): DetailRow[] {
  return byProcedure.map((p) => ({
    name: p.name,
    specialty: p.specialty ?? '—',
    avgPerCase: p.avgPerCase,
    cases: p.cases,
    topModule: p.topModule,
    total: p.total,
    liveCaseRatio: 0,
  }));
}

// Pick the label for the module with the highest raw count. Returns ''
// when every count is zero so callers can decide how to fall back.
function pickTopModule(counts: CanopusSIModuleCounts): string {
  const entries: Array<[ModuleLabel, number]> = [
    ['Reference Guide', counts.refGuide],
    ['Pick List', counts.pickList],
    ['Tray Layout', counts.trayLayout],
    ['Room Layout', counts.roomLayout],
    ['Procedure Notes', counts.procNotes],
  ];
  const top = entries.reduce((best, cur) => (cur[1] > best[1] ? cur : best));
  return top[1] > 0 ? top[0] : '';
}

function adaptByProfile(byProfile: CanopusSIAggregates['byProfile']): ProfileRow[] {
  return byProfile.map((p) => {
    const total = p.total || 1;
    return {
      name: p.name,
      surgeon: p.surgeonName ?? '—',
      procedure: p.procedureName ?? '—',
      avgPerCase: p.avgPerCase,
      cases: p.cases,
      monthlyAvg: 0, // no monthly/profile bucketing server-side yet
      total: p.total,
      topModule: pickTopModule(p.modules),
      modules: {
        refGuide: Math.round((p.modules.refGuide / total) * 100),
        pickList: Math.round((p.modules.pickList / total) * 100),
        trayLayout: Math.round((p.modules.trayLayout / total) * 100),
        roomLayout: Math.round((p.modules.roomLayout / total) * 100),
        procNotes: Math.round((p.modules.procNotes / total) * 100),
      },
    };
  });
}

function adaptProfileToDetailRow(profiles: ProfileRow[]): DetailRow[] {
  return profiles.map((p) => ({
    name: p.name,
    surgeon: p.surgeon,
    procedure: p.procedure,
    variant: p.name,
    avgPerCase: p.avgPerCase,
    cases: p.cases,
    topModule: p.topModule,
    total: p.total,
    liveCaseRatio: 0,
  }));
}

/**
 * Friendly labels for CreatorX's DevicePlatform enum (the Unity platform
 * strings stored on device members). Used to name the merged device rows.
 */
const DEVICE_PLATFORM_LABELS: Record<string, string> = {
  IPhonePlayer: 'iPad',
  VisionOS: 'Vision Pro',
  WSAPlayerARM: 'HoloLens 2',
  WindowsEditor: 'Unity Editor (Windows)',
  OSXEditor: 'Unity Editor (macOS)',
};

/**
 * Resolves how a By-Staff entry should be displayed. Shared OR devices come
 * through merged into one row per platform (with raw position="Device"),
 * which would otherwise read like a data bug. For those we name the row by
 * device platform (e.g. "iPad", "Vision Pro") and, under the "Shared Devices"
 * subsection, show the merged count in the role ("N shared devices"). Pure +
 * exported so the relabel logic is unit-testable without rendering the tab.
 */
export function staffRowDisplay(s: {
  name: string | null;
  userId: string;
  position: string | null;
  isDevice: boolean;
  devicePlatform?: string | null;
  deviceCount?: number;
}): { name: string; role: string; isDevice: boolean } {
  if (s.isDevice) {
    const label =
      (s.devicePlatform && DEVICE_PLATFORM_LABELS[s.devicePlatform]) || s.name || 'Shared device';
    const count = s.deviceCount ?? 0;
    return {
      name: label,
      role: count > 1 ? `${count} shared devices` : 'Shared device',
      isDevice: true,
    };
  }
  return {
    name: capitalizeName(s.name) ?? s.userId,
    role: s.position ?? '—',
    isDevice: false,
  };
}

function adaptByStaff(byStaff: CanopusSIAggregates['byStaff']): DetailRow[] {
  return byStaff.map((s) => {
    const display = staffRowDisplay(s);
    return {
      name: display.name,
      position: display.role,
      isDevice: display.isDevice,
      total: s.total,
      offCase: s.offCase,
      avgPerCase: s.avgPerCase,
      cases: s.cases,
      topModule: s.topModule,
      liveCaseRatio: 0,
    };
  });
}

function formatMonthYearLabel(date: Date): string {
  return `${date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })} ${date.getUTCFullYear()}`;
}

function formatMonthAxisLabel(monthLabel: string): string {
  return monthLabel.split(' ')[0] ?? monthLabel;
}

function getMonthToDateLabel(
  generatedAtIso: string | null | undefined,
  monthlyTrendData: CanopusSIAggregates['monthlyTrend']
): string | null {
  if (!generatedAtIso || !monthlyTrendData || monthlyTrendData.length === 0) return null;

  const generatedAt = new Date(generatedAtIso);
  if (Number.isNaN(generatedAt.getTime())) return null;

  const currentMonthLabel = formatMonthYearLabel(generatedAt);
  const lastMonthLabel = monthlyTrendData[monthlyTrendData.length - 1]?.month;

  return lastMonthLabel === currentMonthLabel ? `${currentMonthLabel} is month-to-date` : null;
}

// ─── MedalIcon (mockup line 6744) ─────────────────────────────────
function MedalIcon({ rank }: { rank: number }) {
  const colors: Record<number, string> = {
    1: '#FFD700',
    2: '#C0C0C0',
    3: '#CD7F32',
  };
  if (rank > 3) return <span className="si-medal-rank">{rank}</span>;
  // medal background + border driven by metallic per-rank color — kept inline.
  return (
    <div
      className="si-medal"
      style={{
        background: `${colors[rank]}22`,
        border: `2px solid ${colors[rank]}`,
      }}
    >
      <span className="si-medal-num" style={{ color: colors[rank] }}>
        {rank}
      </span>
    </div>
  );
}

// ─── SI × TFI helpers ─────────────────────────────────────────────
// Threshold-based color per the design: >=75 green, >=50 cyan, >=25 amber,
// else deep amber/orange. Used for both the TFI cell and each Layers pill.
function tfiThresholdColor(n: number): string {
  if (n >= 75) return '#10B981';
  if (n >= 50) return '#06B6D4';
  if (n >= 25) return '#F59E0B';
  return '#FFB300';
}

interface SITfiStatus {
  label: string;
  color: string;
  background: string;
}

// Status pill computation — mirrors the mockup logic.
function computeSITfiStatus(row: DemoSITfiRow): SITfiStatus {
  if (row.expected === null) {
    // Not yet active — building benchmark.
    return {
      label: 'Building Benchmark',
      color: '#6366F1',
      background: 'rgba(99, 102, 241, 0.15)',
    };
  }
  const delta = row.siToday - row.expected;
  if (delta > 5) {
    return { label: 'Above Expected', color: '#F59E0B', background: 'rgba(245, 158, 11, 0.15)' };
  }
  if (delta < -5) {
    return { label: 'Below Expected', color: '#10B981', background: 'rgba(16, 185, 129, 0.15)' };
  }
  return { label: 'On Track', color: '#6366F1', background: 'rgba(99, 102, 241, 0.15)' };
}

// ─── SITfiCorrelationCard ─────────────────────────────────────────
export function SITfiCorrelationCard({ rows }: { rows: DemoSITfiRow[] }) {
  const sorted = rows.slice().sort((a, b) => a.tfi - b.tfi);
  return (
    <div className="si-tfi-card">
      <SectionLabel text="SI × TFI Correlation" />
      <div className="si-tfi-description">
        Lower Team Familiarity Index (TFI) scores correlate with higher SI density. Staff less
        familiar with a surgeon or procedure generate more inquiries as they reference procedural
        knowledge during surgery. This pattern is expected and not an indicator of risk — it
        reflects normal learning and validation behavior.
      </div>
      {sorted.length === 0 ? (
        <div className="si-tfi-empty">
          Building TFI data — this section will populate as TFI scores accumulate.
        </div>
      ) : (
        <table className="si-tfi-table">
          <thead>
            <tr>
              <th className="si-tfi-th">Staff</th>
              <th className="si-tfi-th si-tfi-th--right">TFI</th>
              <th className="si-tfi-th si-tfi-th--center">Layers</th>
              <th className="si-tfi-th si-tfi-th--right">SIs Today</th>
              <th className="si-tfi-th si-tfi-th--right">Off-Case</th>
              <th className="si-tfi-th si-tfi-th--right">Expected</th>
              <th className="si-tfi-th si-tfi-th--center">Status</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => {
              const tfiColor = tfiThresholdColor(row.tfi);
              const status = computeSITfiStatus(row);
              const isBuilding = row.expected === null;
              return (
                <tr key={i} className="si-tfi-row">
                  <td className="si-tfi-td si-tfi-td--name">
                    {capitalizeName(row.name) ?? row.name}
                  </td>
                  {/* TFI threshold color is data-driven — kept inline. */}
                  <td className="si-tfi-td si-tfi-td--right" style={{ color: tfiColor }}>
                    {row.tfi}
                  </td>
                  <td className="si-tfi-td si-tfi-td--center">
                    <span className="si-tfi-layers">
                      {row.layers.map((layerVal, lIdx) => {
                        const lColor = tfiThresholdColor(layerVal);
                        return (
                          <span
                            key={lIdx}
                            className="si-tfi-layer-pill"
                            /* Threshold-driven layer colors — inline. */
                            style={{
                              background: `${lColor}22`,
                              color: lColor,
                              border: `1px solid ${lColor}55`,
                            }}
                          >
                            {layerVal}
                          </span>
                        );
                      })}
                    </span>
                  </td>
                  <td className="si-tfi-td si-tfi-td--si-today">{row.siToday}</td>
                  <td
                    className={`si-tfi-td ${
                      row.siOffCase > 0 ? 'si-tfi-td--off-has' : 'si-tfi-td--off-zero'
                    }`}
                  >
                    {row.siOffCase}
                  </td>
                  <td
                    className={`si-tfi-td si-tfi-td--expected ${
                      isBuilding ? 'si-tfi-td--expected-building' : ''
                    }`}
                  >
                    {row.expected !== null
                      ? row.expected
                      : row.remainingForBenchmark !== null
                        ? 'Building'
                        : '--'}
                  </td>
                  <td className="si-tfi-td si-tfi-td--center">
                    {/* Status pill color/background is semantic per data — kept inline. */}
                    <span
                      className="si-tfi-status-pill"
                      style={{ color: status.color, background: status.background }}
                    >
                      {status.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── SurgicalInquiriesTab (mockup line 7339) ──────────────────────
export function SurgicalInquiriesTab({
  facilityId,
  /** UK facilities use 'OT' (Operating Theatre); everywhere else 'OR'. */
  roomLabel = 'OR',
  // when true, skip the SI aggregates fetch and use demo dataset.
  demoMode = false,
  // real staff hoisted to the shell — used to populate
  // byStaff rows with real names in demo mode.
  realStaff = null,
  // real surgeons hoisted to the shell — used to populate
  // surgeon-keyed sections (byOR, bySurgeon, byProfile) in demo mode.
  realSurgeons = null,
}: {
  facilityId: string | null;
  roomLabel?: string;
  demoMode?: boolean;
  realStaff?: CanopusStaffStatus | null;
  realSurgeons?: InsightsSurgeon[] | null;
}) {
  const [period, setPeriod] = useState<CanopusSIPeriod>('today');
  const [detailView, setDetailView] = useState<DetailView>('surgeon');
  const [agg, setAgg] = useState<CanopusSIAggregates | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    if (!facilityId) {
      setAgg(null);
      setIsLoading(false);
      return;
    }
    // Demo facility short-circuit. Pass the baked staff and
    // surgeon lists so all name-keyed sections render real names.
    if (demoMode) {
      setIsLoading(false);
      setAgg(getDemoSIAggregates(period, realStaff?.members ?? null, realSurgeons));
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    canopusCommandService
      .getSurgicalInquiriesAggregates(facilityId, period)
      .then((data) => {
        if (!cancelled) setAgg(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [facilityId, period, demoMode, realStaff, realSurgeons]);

  const moduleColors: Record<ModuleLabel, string> = {
    'Reference Guide': '#0FB7D1',
    'Pick List': '#6366F1',
    'Tray Layout': '#FBBF24',
    'Room Layout': '#EC4899',
    'Procedure Notes': '#8B5CF6',
  };
  const periodData = agg
    ? adaptAggregatesToPeriodData(agg.overview)
    : { total: 0, avgPerCase: 0, liveCaseRatio: 0, cases: 0, perProfile: '--', offCase: 0 };
  const todayByModule = agg
    ? adaptModuleCounts(agg.byModule)
    : {
        'Reference Guide': 0,
        'Pick List': 0,
        'Tray Layout': 0,
        'Room Layout': 0,
        'Procedure Notes': 0,
      };
  const todayTotal = agg ? agg.overview.total : 0;

  const periodLabels: Record<CanopusSIPeriod, string> = {
    today: 'Today',
    '7d': '7 Days',
    '30d': '30 Days',
    '90d': '90 Days',
    ytd: 'YTD',
    all: 'All Time',
  };

  const heatmapData = agg && agg.heatmap && agg.heatmap.length > 0 ? agg.heatmap : [];
  const timingData = agg
    ? {
        intraOp: agg.timing.intraOperative.pct,
        preOp: agg.timing.preOperative.pct,
        outsideSurgery: agg.timing.outsideSurgery.pct,
        postOp: agg.timing.postOperative.pct,
      }
    : { intraOp: 0, preOp: 0, outsideSurgery: 0, postOp: 0 };
  const monthlyTrendData =
    agg && agg.monthlyTrend && agg.monthlyTrend.length > 0 ? agg.monthlyTrend : [];
  const maxMonthlyTrendTotal =
    monthlyTrendData.length > 0 ? Math.max(...monthlyTrendData.map((month) => month.total)) : 0;
  const monthToDateLabel = agg ? getMonthToDateLabel(agg.generatedAtIso, monthlyTrendData) : null;

  const profileData = agg ? adaptByProfile(agg.byProfile) : [];
  const pickDetail = (): DetailRow[] => {
    if (!agg) return [];
    if (detailView === 'surgeon') return adaptBySurgeon(agg.bySurgeon);
    if (detailView === 'specialty') return adaptBySpecialty(agg.bySpecialty);
    if (detailView === 'procedure') return adaptByProcedure(agg.byProcedure);
    if (detailView === 'profile') return adaptProfileToDetailRow(adaptByProfile(agg.byProfile));
    if (detailView === 'staff') return adaptByStaff(agg.byStaff);
    return adaptByOR(agg.byOR).map((r) => ({
      name: r.or,
      total: r.total,
      avgPerCase: r.avgPerCase,
      cases: r.cases,
      topModule: r.topModule,
      liveCaseRatio: 0,
    }));
  };
  const detailData = pickDetail();
  const sortedDetail = [...detailData].sort((a, b) => b.total - a.total);
  // Staff view splits real people from shared devices so the latter render
  // under their own "Shared Devices" subsection rather than mixed into the
  // leaderboard ranking.
  const staffPeople = sortedDetail.filter((r) => !r.isDevice);
  const staffDevices = sortedDetail.filter((r) => r.isDevice);

  const renderDetailRow = (row: DetailRow, rank: number, key: React.Key) => (
    <tr key={key} className="si-row">
      <td className="si-td si-td--rank">
        {/* Device rows aren't ranked against staff — no medal. */}
        {row.isDevice ? null : <MedalIcon rank={rank} />}
      </td>
      {detailView === 'profile' ? (
        <>
          <td className="si-td si-td--name">{row.surgeon ?? '—'}</td>
          <td className="si-td si-td--meta">{row.procedure ?? '—'}</td>
          <td className="si-td si-td--meta">{row.variant ?? row.name}</td>
        </>
      ) : (
        <td className="si-td si-td--name">
          {row.isDevice ? (
            <span className="si-device-name">
              <Tablet size={12} className="si-device-icon" />
              {row.name}
            </span>
          ) : (
            row.name
          )}
        </td>
      )}
      {detailView === 'staff' && <td className="si-td si-td--meta">{row.position}</td>}
      <td className="si-td si-td--total-num">{row.total.toLocaleString()}</td>
      {detailView === 'staff' && (
        <td className={`si-td ${(row.offCase ?? 0) > 0 ? 'si-td--off-has' : 'si-td--off-zero'}`}>
          {row.offCase != null ? row.offCase.toLocaleString() : '--'}
        </td>
      )}
      <td className="si-td si-td--secondary-r">{row.avgPerCase}</td>
      <td className="si-td si-td--meta-r">{row.cases}</td>
      {/* topModule color is module-specific — kept inline */}
      <td
        className="si-td si-td--module"
        style={{ color: moduleColors[row.topModule as ModuleLabel] || '#8899AA' }}
      >
        {row.topModule}
      </td>
    </tr>
  );

  return (
    <div>
      <div className="si-info-banner">
        A <span className="si-info-banner-em">Surgical Inquiry (SI)</span> is a discrete knowledge
        retrieval event — each time a team member accesses a specific piece of procedural knowledge
        through eXeX. The average number of SIs per surgery varies by facility and is computed
        dynamically. For the selected period ({periodLabels[period]}), this facility averages{' '}
        <span className="si-info-banner-em">{periodData.avgPerCase} SIs</span> per case across
        Reference Guides, Pick Lists, Tray Layouts, Room Layouts, and Procedure Notes.
      </div>
      <div className="si-period-tabs">
        {(Object.entries(periodLabels) as Array<[CanopusSIPeriod, string]>).map(([k, v]) => (
          <button
            key={k}
            onClick={() => setPeriod(k)}
            className={`si-period-btn ${period === k ? 'si-period-btn--active' : ''}`}
          >
            {v}
          </button>
        ))}
      </div>
      <div className="si-metrics">
        <M
          label="SIs Delivered"
          value={periodData.total.toLocaleString()}
          color="#0FB7D1"
          loading={isLoading}
        />
        <M
          label="Off-Case SIs"
          value={periodData.offCase.toLocaleString()}
          sub="Outside active surgery"
          color="#8B5CF6"
          loading={isLoading}
        />
        <M
          label="Avg SI / Procedure"
          value={periodData.avgPerCase}
          color="#6366F1"
          loading={isLoading}
        />
        <M label="Cases Recorded" value={periodData.cases} loading={isLoading} />
        {period !== 'today' && (
          <M
            label="SI / Profile / Month"
            value={periodData.perProfile}
            sub="Avg across all profiles"
            color="#EC4899"
            loading={isLoading}
          />
        )}
      </div>
      <div className="si-grid">
        <div className="si-card">
          <SectionLabel text="SIs by Module" />
          <div className={isLoading ? 'loading-blur' : undefined}>
            <div className="si-module-bar">
              {(Object.entries(todayByModule) as Array<[ModuleLabel, number]>).map(([mod, val]) => (
                <div
                  key={mod}
                  className="si-module-bar-cell"
                  style={{
                    flex: val,
                    background: moduleColors[mod],
                    minWidth: val > 0 ? 4 : 0,
                  }}
                />
              ))}
            </div>
            {(Object.entries(todayByModule) as Array<[ModuleLabel, number]>).map(([mod, val]) => (
              <div key={mod} className="si-module-row">
                <div className="si-module-name-wrap">
                  <div className="si-module-swatch" style={{ background: moduleColors[mod] }} />
                  <span className="si-module-name">{mod}</span>
                </div>
                <div className="si-module-val-wrap">
                  <span className="si-module-val">{val}</span>
                  <span className="si-module-pct">
                    {todayTotal > 0 ? Math.round((val / todayTotal) * 100) : 0}%
                  </span>
                </div>
              </div>
            ))}
            <div className="si-module-total-row">
              <span className="si-module-total-label">Total</span>
              <span className="si-module-total-val">{todayTotal}</span>
            </div>
          </div>
        </div>
        <div className="si-card">
          <SectionLabel text="Timing Context" />
          <div className={isLoading ? 'loading-blur' : undefined}>
            <div className="si-timing-sub">When SIs occur relative to surgery</div>
            {[
              {
                label: 'Intra-Operative',
                pct: timingData.intraOp,
                color: '#0FB7D1',
                desc: 'During live surgery',
              },
              {
                label: 'Pre-Operative',
                pct: timingData.preOp,
                color: '#6366F1',
                desc: 'Before case starts',
              },
              {
                label: 'Off Case',
                pct: timingData.outsideSurgery,
                color: '#FBBF24',
                desc: 'No active case — prep, review, training',
              },
            ].map((t) => (
              <div key={t.label} className="si-timing-row">
                <div className="si-timing-head">
                  <span className="si-timing-label">{t.label}</span>
                  <span className="si-timing-pct" style={{ color: t.color }}>
                    {t.pct}%
                  </span>
                </div>
                <div className="si-timing-track">
                  <div
                    className="si-timing-fill"
                    style={{ width: `${t.pct}%`, background: t.color }}
                  />
                </div>
                <div className="si-timing-desc">{t.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SI × TFI Correlation. In demo mode we synthesize
          dummy TFI / layers / expected values from the on-duty demo staff;
          outside demo mode the card renders the "Building TFI data" empty
          state until a real TFI source is wired up. */}
      <SITfiCorrelationCard rows={demoMode ? getDemoSITfiRows(realStaff?.members ?? null) : []} />

      <div className="si-detail-tabs">
        {(
          [
            { id: 'surgeon', label: 'By Surgeon' },
            { id: 'staff', label: 'By Staff' },
            { id: 'or', label: `By ${roomLabel}` },
            { id: 'specialty', label: 'By Specialty' },
            { id: 'procedure', label: 'By Procedure' },
            { id: 'profile', label: 'By Profile' },
          ] as Array<{ id: DetailView; label: string }>
        ).map((v) => (
          <button
            key={v.id}
            onClick={() => setDetailView(v.id)}
            className={`si-detail-btn ${detailView === v.id ? 'si-detail-btn--active' : ''}`}
          >
            {v.label}
          </button>
        ))}
      </div>
      <div className="si-card si-card--mb">
        <SectionLabel
          text={`SI Leaders — ${
            detailView === 'surgeon'
              ? 'Surgeons'
              : detailView === 'staff'
                ? 'Staff'
                : detailView === 'or'
                  ? `${roomLabel}s`
                  : detailView === 'specialty'
                    ? 'Specialties'
                    : detailView === 'procedure'
                      ? 'Procedures'
                      : 'Surgical Profiles'
          }`}
        />
        <table className="si-table">
          <thead>
            <tr>
              <th className="si-th">#</th>
              {detailView === 'profile' ? (
                <>
                  <th className="si-th">Surgeon</th>
                  <th className="si-th">Procedure</th>
                  <th className="si-th">Variant</th>
                </>
              ) : (
                <th className="si-th">Name</th>
              )}
              {detailView === 'staff' && <th className="si-th">Role</th>}
              <th className="si-th si-th--right">Total SIs</th>
              {detailView === 'staff' && <th className="si-th si-th--right">Off-Case</th>}
              <th className="si-th si-th--right">Avg / Case</th>
              <th className="si-th si-th--right">Cases</th>
              <th className="si-th">Top Module</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && detailData.length === 0 ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={`skel-detail-${i}`} className="si-row">
                  <td className="loading-blur si-td si-td--rank">{i + 1}</td>
                  {detailView === 'profile' ? (
                    <>
                      <td className="loading-blur si-td si-td--name">Surgeon</td>
                      <td className="loading-blur si-td si-td--meta">Procedure</td>
                      <td className="loading-blur si-td si-td--meta">Variant</td>
                    </>
                  ) : (
                    <td className="loading-blur si-td si-td--name">Name</td>
                  )}
                  {detailView === 'procedure' && (
                    <td className="loading-blur si-td si-td--meta">Specialty</td>
                  )}
                  {(detailView === 'surgeon' || detailView === 'staff') && (
                    <td className="loading-blur si-td si-td--meta">Surgeon</td>
                  )}
                  {detailView === 'staff' && (
                    <td className="loading-blur si-td si-td--meta">Role</td>
                  )}
                  <td className="loading-blur si-td si-td--accent">0</td>
                  {detailView === 'staff' && (
                    <td className="loading-blur si-td si-td--purple">0</td>
                  )}
                  <td className="loading-blur si-td si-td--violet">0</td>
                  <td className="loading-blur si-td si-td--amber">0</td>
                  <td className="loading-blur si-td si-td--pink">0</td>
                </tr>
              ))
            ) : detailView === 'staff' ? (
              <>
                {staffPeople.map((row, i) => renderDetailRow(row, i + 1, `staff-${i}`))}
                {staffDevices.length > 0 && (
                  <tr className="si-subhead-row">
                    {/* staff view columns: #, Name, Role, Total, Off-Case, Avg, Cases, Top Module */}
                    <td className="si-subhead" colSpan={8}>
                      Shared Devices
                    </td>
                  </tr>
                )}
                {staffDevices.map((row, i) => renderDetailRow(row, i + 1, `device-${i}`))}
              </>
            ) : (
              sortedDetail.map((row, i) => renderDetailRow(row, i + 1, i))
            )}
          </tbody>
        </table>
      </div>
      {detailView === 'profile' && (
        <div className="si-card si-card--mb">
          <SectionLabel text="Surgical Profiles" />
          <div className={isLoading ? 'loading-blur' : undefined}>
            <div className="si-text-mini">Module distribution per profile</div>
            <table className="si-table">
              <thead>
                <tr>
                  <th className="si-th">Profile</th>
                  {(Object.entries(moduleColors) as Array<[ModuleLabel, string]>).map(
                    ([mod, col]) => (
                      <th
                        key={mod}
                        className="si-th si-th--right"
                        /* Column header tinted with the module color. */
                        style={{ color: col }}
                      >
                        {mod}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {profileData.map((p, i) => (
                  <tr key={i} className="si-row">
                    <td className="si-td si-td--name">{p.name}</td>
                    {(Object.entries(moduleColors) as Array<[ModuleLabel, string]>).map(
                      ([mod, col]) => {
                        const key =
                          mod === 'Reference Guide'
                            ? 'refGuide'
                            : mod === 'Pick List'
                              ? 'pickList'
                              : mod === 'Tray Layout'
                                ? 'trayLayout'
                                : mod === 'Room Layout'
                                  ? 'roomLayout'
                                  : 'procNotes';
                        return (
                          <td
                            key={mod}
                            className="si-td si-profile-pct-cell"
                            /* Percentage cell tinted with the module color. */
                            style={{ color: col }}
                          >
                            {p.modules[key]}%
                          </td>
                        );
                      }
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="si-grid">
        <div className="si-card">
          <SectionLabel text={'SI Heat Map — Rolling 30 Days'} />
          <div className={isLoading ? 'loading-blur' : undefined}>
            <div className="si-text-mini">
              Average daily SIs by day of week (including weekends)
            </div>
            <div className="si-heatmap">
              {heatmapData.length === 0 ? (
                <div className="si-heatmap-empty">No heatmap data available</div>
              ) : (
                heatmapData.map((d) => {
                  const maxVal = Math.max(...heatmapData.map((x) => x.total));
                  const intensity = d.total / maxVal;
                  const isTop = d.total === maxVal;
                  return (
                    <div key={d.day} className="si-heatmap-cell">
                      {/* heatmap intensity is data-driven; background interpolates accent. */}
                      <div
                        className="si-heatmap-block"
                        style={{
                          background: isTop
                            ? 'rgba(15,183,209,0.2)'
                            : `rgba(15,183,209,${0.04 + intensity * 0.12})`,
                          border: isTop
                            ? '1px solid rgba(15,183,209,0.4)'
                            : '1px solid transparent',
                        }}
                      >
                        <div
                          className="si-heatmap-num"
                          style={{ color: isTop ? '#0FB7D1' : '#C8D8E8' }}
                        >
                          {d.total}
                        </div>
                      </div>
                      <div
                        className="si-heatmap-label"
                        style={{
                          color: isTop ? '#0FB7D1' : '#667788',
                          fontWeight: isTop ? 600 : 400,
                        }}
                      >
                        {d.day}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
        <div className="si-card">
          <SectionLabel text="Monthly SI Growth" />
          <div className={isLoading ? 'loading-blur' : undefined}>
            <div className="si-text-mini">Total SIs delivered per month</div>
            {monthlyTrendData.length === 0 ? (
              <div className="si-heatmap-empty">No monthly trend data available</div>
            ) : (
              monthlyTrendData.map((m, i) => {
                const isLast = i === monthlyTrendData.length - 1;
                const pct = maxMonthlyTrendTotal > 0 ? (m.total / maxMonthlyTrendTotal) * 100 : 0;
                return (
                  <div key={m.month} className="si-monthly-row">
                    <span className="si-monthly-label">{formatMonthAxisLabel(m.month)}</span>
                    <div className="si-monthly-track">
                      {/* width encodes data %; last bar gets a stronger fill. */}
                      <div
                        className="si-monthly-fill"
                        style={{
                          width: `${pct}%`,
                          background: isLast ? 'rgba(15,183,209,0.5)' : 'rgba(15,183,209,0.25)',
                        }}
                      >
                        <span className="si-monthly-fill-num">{m.total.toLocaleString()}</span>
                      </div>
                    </div>
                    <span className="si-monthly-side">{m.avgPerCase}/case</span>
                  </div>
                );
              })
            )}
            {monthToDateLabel ? <div className="si-mtd-note">{monthToDateLabel}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
